import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import { ApolloError } from '@apollo/client'
import { WS_URL, client } from '../gql/client'
import {
  GET_SIDEBAR_COUNTERS,
  GET_UNREAD_NOTIFICATIONS_BY_TYPE,
} from '../gql/queries/notificationsQuery'
import {
  IGetUnreadNotificationsByTypeResponse,
  INotificationTypeCount,
} from '../types/notifications'
import { STORAGE_NAMES } from '../helpers/consts'
import { useGetUser } from '../store/userStore'
import { useRealtimeStore } from '../store/realtimeStore'
import { GENERATE_WS_TICKET } from '../gql/mutations/authMutations'

import { GlobalNotificationHandler } from './GlobalNotificationHandler'

type SidebarCountersResponse = {
  sidebarCounters?: {
    unreadNotificationsCount?: number
    openTicketsCount?: number
    inboxUnreadCount?: number
    policyChatUnreadCount?: number
  }
}

const RECONNECT_DELAY_MS = 5000

export const WebSocketContext = createContext<WebSocket | null>(null)

const getSocketUrl = (token: string) => {
  const normalizedBaseUrl = WS_URL.endsWith('/') ? WS_URL.slice(0, -1) : WS_URL
  return `${normalizedBaseUrl}/ws/base?authorization=${encodeURIComponent(`Bearer ${token}`)}`
}

const isLikelyAuthError = (error: unknown) => {
  if (!(error instanceof ApolloError)) return false
  const hasExpiredGraphQLError = error.graphQLErrors.some((graphQLError) =>
    String(graphQLError?.message || '').includes('Signature has expired')
  )
  return hasExpiredGraphQLError || Boolean(error.networkError)
}

export const WebSocketProvider = ({ children }: { children: ReactNode }) => {
  const user = useGetUser((state) => state.user)
  const status = useGetUser((state) => state.status)
  const setConnectionStatus = useRealtimeStore((state) => state.setConnectionStatus)
  const syncSidebarCounters = useRealtimeStore((state) => state.syncSidebarCounters)
  const syncUnreadNotificationsByType = useRealtimeStore(
    (state) => state.syncUnreadNotificationsByType
  )
  const handleSocketEvent = useRealtimeStore((state) => state.handleSocketEvent)
  const resetRealtimeState = useRealtimeStore((state) => state.reset)
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)

  const token = useMemo(
    () => user?.token || window.localStorage.getItem(STORAGE_NAMES.TOKEN) || '',
    [user?.token]
  )

  useEffect(() => {
    if (status !== 'user' || !token) {
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      socketRef.current?.close()
      socketRef.current = null
      resetRealtimeState()
      return
    }

    let isUnmounted = false

    const hydrateCounters = async () => {
      try {
        const [sidebarCountersResult, unreadByTypeResult] = await Promise.all([
          client.query<SidebarCountersResponse>({
            query: GET_SIDEBAR_COUNTERS,
            fetchPolicy: 'network-only',
          }),
          client.query<IGetUnreadNotificationsByTypeResponse>({
            query: GET_UNREAD_NOTIFICATIONS_BY_TYPE,
            fetchPolicy: 'network-only',
          }),
        ])

        if (isUnmounted) return

        syncSidebarCounters({
          unreadNotificationsCount:
            sidebarCountersResult.data?.sidebarCounters?.unreadNotificationsCount ?? 0,
          openTicketsCount: sidebarCountersResult.data?.sidebarCounters?.openTicketsCount ?? 0,
          inboxUnreadCount: sidebarCountersResult.data?.sidebarCounters?.inboxUnreadCount ?? 0,
          policyChatUnreadCount:
            sidebarCountersResult.data?.sidebarCounters?.policyChatUnreadCount ?? 0,
        })

        syncUnreadNotificationsByType(
          unreadByTypeResult.data?.unreadNotificationsByType || ([] as INotificationTypeCount[])
        )
      } catch (error) {
        if (!isLikelyAuthError(error)) {
          console.error('Unable to hydrate realtime counters', error)
        }
      }
    }

    const connect = async () => {
      if (isUnmounted) return

      setConnectionStatus('connecting')

      let wsUrl = ''
      try {
        const ticketResult = await client.mutate({
          mutation: GENERATE_WS_TICKET,
        })
        const ticket = ticketResult.data?.generateWsTicket?.ticket

        const normalizedBaseUrl = WS_URL.endsWith('/') ? WS_URL.slice(0, -1) : WS_URL
        if (ticket) {
          wsUrl = `${normalizedBaseUrl}/ws/base?ticket=${ticket}`
        } else {
          wsUrl = getSocketUrl(token)
        }
      } catch (error) {
        console.error('Failed to generate WebSocket ticket, falling back to token auth', error)
        wsUrl = getSocketUrl(token)
      }

      if (isUnmounted) return

      const socket = new WebSocket(wsUrl)
      socketRef.current = socket

      socket.onopen = () => {
        if (isUnmounted) return
        setConnectionStatus('connected')
      }

      socket.onmessage = (messageEvent) => {
        try {
          const parsedEvent = JSON.parse(messageEvent.data)
          handleSocketEvent(parsedEvent)
        } catch (error) {
          console.error('Unable to parse global websocket event', error)
        }
      }

      socket.onerror = () => {
        if (isUnmounted) return
        setConnectionStatus('disconnected')
      }

      socket.onclose = () => {
        if (isUnmounted) return

        socketRef.current = null
        setConnectionStatus('disconnected')
        reconnectTimeoutRef.current = window.setTimeout(connect, RECONNECT_DELAY_MS)
      }
    }

    hydrateCounters()
    connect()

    return () => {
      isUnmounted = true

      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      const activeSocket = socketRef.current
      socketRef.current = null
      if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
        activeSocket.close()
      } else {
        activeSocket?.close()
      }
    }
  }, [
    handleSocketEvent,
    resetRealtimeState,
    setConnectionStatus,
    status,
    syncSidebarCounters,
    syncUnreadNotificationsByType,
    token,
  ])

  return (
    <WebSocketContext.Provider value={socketRef.current}>
      {children}
      <GlobalNotificationHandler />
    </WebSocketContext.Provider>
  )
}

export const useGlobalWebSocket = () => useContext(WebSocketContext)
